import { useEffect, useState } from 'react';
import {
  PROVISIONING_POLL_GIVE_UP_MS,
  PROVISIONING_POLL_INTERVAL_MS,
  PROVISIONING_POLL_PATIENCE_MS,
  PROVISIONING_POLL_SLOW_INTERVAL_MS,
} from './provisioningPolling';

export type HostReadyState = 'idle' | 'checking' | 'unreachable' | 'ready' | 'recovered' | 'timed-out' | 'stopped';

type Options = {
  // How often to re-probe while success is still expected.
  intervalMs?: number;
  // The gentler cadence once the wait is overdue — still checking, just not eagerly.
  slowIntervalMs?: number;
  // When to surface "taking longer than expected" and ease off. A new org host needs its own
  // Let's Encrypt cert (DNS-01, on the critical path), so ~10 min is normal; this fires only
  // on a genuine overrun.
  timeoutMs?: number;
  // When to stop checking entirely ('stopped'): by now something is genuinely wrong, and a
  // page still polling would overstate the odds. A refresh starts a fresh session.
  giveUpMs?: number;
};

// Polls a freshly-provisioned org host until its edge is live. An un-provisioned slug has no
// TLS cert/route yet, so the connection is reset — a cross-origin fetch REJECTS while the host
// is still building and RESOLVES once it answers. We only care that the connection succeeds,
// so a no-cors (opaque) response is enough: no CORS headers or readiness endpoint required.
//
// 'ready' means up on the very first probe; 'recovered' means up only after at least one failed
// probe. That lets callers redirect seamlessly when the host was already live, but offer a manual
// switch (rather than yanking) when the user has been waiting — they've started working elsewhere.
export function useHostReady(
  host: string | null,
  {
    intervalMs = PROVISIONING_POLL_INTERVAL_MS,
    slowIntervalMs = PROVISIONING_POLL_SLOW_INTERVAL_MS,
    timeoutMs = PROVISIONING_POLL_PATIENCE_MS,
    giveUpMs = PROVISIONING_POLL_GIVE_UP_MS,
  }: Options = {},
): HostReadyState {
  const [state, setState] = useState<{
    host: string;
    value: 'unreachable' | 'ready' | 'recovered' | 'timed-out' | 'stopped';
  } | null>(null);

  useEffect(() => {
    if (!host) return;

    let cancelled = false;
    let sawFailure = false;
    let timer: ReturnType<typeof setInterval>;

    const probe = async () => {
      let up = false;
      try {
        await fetch(`https://${host}/`, { mode: 'no-cors', cache: 'no-store' });
        up = true; // the connection succeeded — the host's edge is live
      } catch {
        up = false; // connection reset / no cert yet — still building
      }
      if (cancelled) return;
      if (up) {
        clearInterval(timer);
        setState({ host, value: sawFailure ? 'recovered' : 'ready' });
      } else {
        sawFailure = true;
        // Report 'unreachable' once, then keep whatever state this host already has: 'timed-out'
        // must survive further failed probes (the "taking longer" message holds while the poll
        // quietly continues), and returning the same object on repeat failures avoids
        // re-rendering the whole route tree every tick.
        setState((s) => (s?.host === host ? s : { host, value: 'unreachable' }));
      }
    };

    // Once overdue, keep checking at the gentle cadence until the give-up mark, then stop for
    // good — from 'stopped' only a refresh (a fresh polling session) can find the host.
    const slowPhase = () => {
      let slowTicks = 0;
      const maxSlowTicks = Math.ceil(Math.max(giveUpMs - timeoutMs, 0) / slowIntervalMs);
      timer = setInterval(() => {
        slowTicks += 1;
        if (slowTicks >= maxSlowTicks) {
          clearInterval(timer);
          if (!cancelled) setState({ host, value: 'stopped' });
          return;
        }
        void probe();
      }, slowIntervalMs);
    };

    let ticks = 0;
    const maxTicks = Math.ceil(timeoutMs / intervalMs);
    timer = setInterval(() => {
      ticks += 1;
      // Timing out changes what the UI says and how eagerly we ask — not whether we ask:
      // a host that comes up during the slow phase still flips to 'recovered' and the
      // switch offer appears.
      if (ticks >= maxTicks) {
        clearInterval(timer);
        if (!cancelled) setState({ host, value: 'timed-out' });
        slowPhase();
        return;
      }
      void probe();
    }, intervalMs);
    void probe();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [host, intervalMs, slowIntervalMs, timeoutMs, giveUpMs]);

  if (!host) return 'idle';
  // No result yet for the current host → the brief pre-first-probe window.
  return state?.host === host ? state.value : 'checking';
}
